import React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

export type WindowType = 'wall' | 'roof' | 'solid_box' | 'canted';

interface WindowTypePromptProps {
  isOpen: boolean;
  onSelect: (windowType: WindowType) => void;
  onClose: () => void;
  isFullscreen?: boolean;
  hideSolidBox?: boolean;
}

const WindowTypePrompt: React.FC<WindowTypePromptProps> = ({ 
  isOpen, 
  onSelect, 
  onClose, 
  isFullscreen = false,
  hideSolidBox = false
}) => {
  const handleWindowTypeSelect = (type: WindowType) => {
    onSelect(type);
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className={`fixed inset-0 bg-gradient-to-br from-slate-900/80 via-slate-800/70 to-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4 ${isFullscreen ? 'z-[10003]' : 'z-[999998]'}`}>
      <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl w-full max-w-md max-h-[85vh] overflow-hidden border border-white/20">
        {/* Modern Header with Gradient */}
        <div className="relative bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 px-5 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2.5">
              <div className="p-1.5 bg-white/20 rounded-lg backdrop-blur-sm">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Window Type</h3>
                <p className="text-blue-100 text-xs">Choose window location</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white hover:bg-white/10 rounded-full p-2 transition-all duration-200"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          <div className="text-center mb-6">
            <h4 className="text-lg font-semibold text-slate-800 mb-2">Select Window Type</h4>
            <p className="text-sm text-slate-600">Choose where the window will be installed</p>
          </div>

          {/* Window Type Options */}
          <div className="space-y-4">
            {/* Wall Window Option */}
            <button
              onClick={() => handleWindowTypeSelect('wall')}
              className="w-full bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 border-2 border-blue-200 hover:border-blue-300 rounded-2xl p-6 transition-all duration-200 text-left group"
            >
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-blue-100 group-hover:bg-blue-200 rounded-xl transition-colors">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <div>
                  <h5 className="text-lg font-bold text-slate-800 mb-1">Wall Window</h5>
                  <p className="text-sm text-slate-600">Standard window installed in exterior walls</p>
                </div>
              </div>
            </button>

            {/* Roof Window Option */}
            <button
              onClick={() => handleWindowTypeSelect('roof')}
              className="w-full bg-gradient-to-r from-orange-50 to-red-50 hover:from-orange-100 hover:to-red-100 border-2 border-orange-200 hover:border-orange-300 rounded-2xl p-6 transition-all duration-200 text-left group"
            >
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-orange-100 group-hover:bg-orange-200 rounded-xl transition-colors">
                  <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5v6l8-8" />
                  </svg>
                </div>
                <div>
                  <h5 className="text-lg font-bold text-slate-800 mb-1">Roof Window</h5>
                  <p className="text-sm text-slate-600">Skylight or roof-mounted window</p>
                </div>
              </div>
            </button>

            {/* Bay Window Option */}
            <button
              onClick={() => handleWindowTypeSelect('canted')}
              className="w-full bg-gradient-to-r from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 border-2 border-purple-200 hover:border-purple-300 rounded-2xl p-6 transition-all duration-200 text-left group"
            >
              <div className="flex items-center space-x-4">
                <div className="bg-purple-100 rounded-full p-3 group-hover:bg-purple-200 transition-colors">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                  </svg>
                </div>
                <div>
                  <h5 className="text-lg font-bold text-slate-800 mb-1">Bay Window</h5>
                  <p className="text-sm text-slate-600">Victorian-style projecting window</p>
                </div>
              </div>
            </button>

            {/* Solid Box Window Option - Hidden for retrofit/energy/estate floorplans */}
            {!hideSolidBox && (
              <button
                onClick={() => handleWindowTypeSelect('solid_box')}
                className="w-full bg-gradient-to-r from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100 border-2 border-green-200 hover:border-green-300 rounded-2xl p-6 transition-all duration-200 text-left group"
              >
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-green-100 group-hover:bg-green-200 rounded-xl transition-colors">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                      <rect x="2" y="4" width="20" height="16" rx="2" strokeWidth={2} fill="none" />
                    </svg>
                  </div>
                  <div>
                    <h5 className="text-lg font-bold text-slate-800 mb-1">Solid Box Window</h5>
                    <p className="text-sm text-slate-600">Bay window extending outside the wall</p>
                  </div>
                </div>
              </button>
            )}
          </div>
        </div>

        {/* Footer with Cancel Button */}
        <div className="bg-slate-50/80 backdrop-blur-sm px-6 py-4 border-t border-slate-200/30">
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-700 font-medium rounded-xl transition-colors duration-200"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default WindowTypePrompt;
